import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useChatContext } from '@company/state/chatContext';
import { useJobAssignmentContext } from '@company/state/jobAssignmentContext';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import { resolveCandidateStage } from '@company/helpers/job-overview-helper';
import { CandidateStage } from '@company/types/job-overview.type';
import { useJobDetailStore } from '@company/pages/JobOverview/DetailV2/stores/jobDetailStore';
import useCandidateFiltersUrlSync from '@company/pages/JobOverview/DetailV2/hooks/useCandidateFiltersUrlSync';

const OPEN_CONVO = 'openConvo';
const JOB_ASSIGNMENT_ID = 'jobAssignmentId';
const WORKER_ID = 'workerId';
const TAB = 'tab';

const parsePositiveId = (raw: string | null): number | null => {
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const isOpenConvoDeepLink = (params: URLSearchParams): boolean =>
  params.get(OPEN_CONVO) === 'true' &&
  parsePositiveId(params.get(JOB_ASSIGNMENT_ID)) != null &&
  parsePositiveId(params.get(WORKER_ID)) != null;

/**
 * URL side-effects for Job Details: candidate tab/filters and the email
 * openConvo deep-link.
 */
export default function useJobDetailUrlSync(): void {
  useCandidateFiltersUrlSync();

  const [searchParams, setSearchParams] = useSearchParams();
  const { workerJobData, setWorkerId } = useJobAssignmentContext();
  const { openChat } = useChatContext();
  const { shouldShowWorkerInfoInChat } = useJobAssignmentStatus()(undefined);
  const setActiveTab = useJobDetailStore((state) => state.setActiveTab);
  const consumedRef = useRef(false);

  const jobAssignmentId = parsePositiveId(searchParams.get(JOB_ASSIGNMENT_ID));
  const workerId = parsePositiveId(searchParams.get(WORKER_ID));
  const shouldOpen = isOpenConvoDeepLink(searchParams);

  useEffect(() => {
    if (!shouldOpen || consumedRef.current || workerId == null) return;
    setWorkerId(workerId);
  }, [shouldOpen, workerId, setWorkerId]);

  useEffect(() => {
    if (!shouldOpen || consumedRef.current || jobAssignmentId == null) return;

    const { worker, jobAssignment } = workerJobData ?? {};
    if (!worker?.id || !jobAssignment) return;
    if (jobAssignment.id !== jobAssignmentId) return;

    consumedRef.current = true;
    const stage = resolveCandidateStage(jobAssignment);
    if (stage) {
      setActiveTab(stage);
    }
    openChat(
      worker,
      jobAssignmentId,
      shouldShowWorkerInfoInChat({ status: jobAssignment.status })
    );

    const next = new URLSearchParams(searchParams);
    next.delete(OPEN_CONVO);
    next.delete(JOB_ASSIGNMENT_ID);
    next.delete(WORKER_ID);
    if (stage === CandidateStage.NEW) {
      next.delete(TAB);
    } else if (stage) {
      next.set(TAB, stage);
    }
    setSearchParams(next, { replace: true });
  }, [
    shouldOpen,
    workerJobData,
    jobAssignmentId,
    openChat,
    searchParams,
    setActiveTab,
    setSearchParams,
    shouldShowWorkerInfoInChat,
  ]);
}
